export const EMI_MIN_INTEREST_RATE = 8;
export const EMI_MAX_INTEREST_RATE = 22;
export const EMI_INTEREST_STEP = 0.1;
export const EMI_DOWN_PAYMENT_STEP = 5000;
/** Configured default rate used across EMI calculator, schedule, and tables. */
export const EMI_DEFAULT_INTEREST_RATE = 10.5;
export const EMI_DEFAULT_TENURE = 60;
export const EMI_SCHEDULE_START_MONTHS = 24;
export const EMI_SCHEDULE_STEP_MONTHS = 12;
export const EMI_SCHEDULE_END_MONTHS = 84;
export const EMI_ALLOWED_TENURES = [12, 24, 36, 48, 60, 72, 84] as const;

/** EMI schedule table tenures: 24 → 84 months, step 12. */
export function getEmiScheduleTenures(): number[] {
    const tenures: number[] = [];
    for (
        let months = EMI_SCHEDULE_START_MONTHS;
        months <= EMI_SCHEDULE_END_MONTHS;
        months += EMI_SCHEDULE_STEP_MONTHS
    ) {
        tenures.push(months);
    }
    return tenures;
}

/** Preferred variant id, else base variant, else first active variant. */
export function resolveDefaultEmiVariant(variants: any[], preferredVariantId?: string | null) {
    const active = variants.filter(
        (item) => item?.is_sanity_done !== false && item?.status !== "discontinued",
    );
    if (!active.length) return null;
    if (preferredVariantId) {
        const preferred = active.find((item) => item._id === preferredVariantId);
        if (preferred) return preferred;
    }
    return active.find((item) => item.is_base_variant) ?? active[0] ?? null;
}

export type EmiTenure = (typeof EMI_ALLOWED_TENURES)[number];

export interface EmiCalculationInput {
    price: number;
    downPayment?: number;
    interestRate?: number;
    tenureMonths?: number;
}

export interface EmiCalculationResult {
    emi: number;
    loanAmount: number;
    totalPayable: number;
}

export interface EmiBreakupData extends EmiCalculationResult {
    price: number;
    location: string;
    downPayment: number;
    interestRate: number;
    tenureMonths: number;
}

const roundToNearestRupee = (value: number) => Math.round(value);

export function clampNumber(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

export function getDefaultDownPayment(price: number): number {
    if (!Number.isFinite(price) || price <= 0) {
        return 0;
    }

    return Math.round(price * 0.1);
}

export function calculateEMI({
    price,
    downPayment = getDefaultDownPayment(price),
    interestRate = EMI_DEFAULT_INTEREST_RATE,
    tenureMonths = EMI_DEFAULT_TENURE   ,
}: EmiCalculationInput): EmiCalculationResult {
    if (!Number.isFinite(price) || price <= 0) {
        return { emi: 0, loanAmount: 0, totalPayable: 0 };
    }

    if (!Number.isFinite(downPayment) || downPayment < 0 || downPayment > price) {
        return { emi: 0, loanAmount: 0, totalPayable: 0 };
    }

    if (!Number.isFinite(interestRate) || interestRate < 0) {
        return { emi: 0, loanAmount: 0, totalPayable: 0 };
    }

    if (!Number.isFinite(tenureMonths) || tenureMonths <= 0) {
        return { emi: 0, loanAmount: 0, totalPayable: 0 };
    }

    const loanAmount = Math.max(0, price - downPayment);
    const monthlyRate = interestRate / 12 / 100;

    if (loanAmount === 0) {
        return { emi: 0, loanAmount: 0, totalPayable: 0 };
    }

    const emi =
        monthlyRate === 0
            ? roundToNearestRupee(loanAmount / tenureMonths)
            : roundToNearestRupee(
                  (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, tenureMonths)) /
                      (Math.pow(1 + monthlyRate, tenureMonths) - 1)
              );

    return {
        emi,
        loanAmount,
        totalPayable: emi * tenureMonths,
    };
}

export function buildEmiBreakup(data: EmiBreakupData): EmiBreakupData {
    return data;
}

export function formatEmiCurrency(value: number): string {
    return new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
    }).format(Math.round(value));
}
